/**
 * @file HeaderTab.tsx
 * @description Вкладка «Шапка» (phase19.16 Р1; SB13, SB67, SB73, SB90;
 * uiux-design-r1 §2): место логотипа четырьмя карточками, «логотип крупнее
 * шапки» и сколько пунктов слева от логотипа (при «по центру»), заливка,
 * состав бренда, регистр пунктов, «закреплять», замки «Вход в кабинет» и
 * «Корзина». Поиска в шапке нет (SB67).
 *
 * Решение 2026-10-11 (дополнение SB8, SB120): «Цвет: светлая/тёмная»
 * заменён «Заливкой» — роль темы или свой цвет; тон пишется вместе с
 * заливкой. «Прозрачная поверх фото» — отдельный флажок: заливка тогда
 * задаёт вид шапки после прокрутки и на страницах без фото.
 *
 * SB122 (uiux-settings-groups §5): группы-карточки Расположение (+
 * «Закреплять»), Заливка, Бренд в шапке, Пункты меню, Всегда в шапке
 * (только просмотр, свёрнута по умолчанию); память —
 * `commora.settings-groups.header`.
 *
 * SB120 этап 4: у «Корзины со счётчиком» — ссылка «Вид корзины →» на экран
 * «Корзина» с панелью «Вид корзины» (uiux-cart §3).
 */

import { fillContrastIssue, resolveSurfaceFill, type SiteColors, type SiteTheme } from "@chaiberi/shared";
import { Link } from "react-router";

import { Label } from "../../ui/label";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { FieldHint, SettingsGroup, SettingsGroups } from "../../ui/settings-groups";
import { CheckRow } from "../CheckRow";
import { OptionCards } from "../OptionCards";
import { Segmented } from "../Segmented";
import { SurfaceFillField } from "../SurfaceFillField";
import { BrandModeList } from "./BrandModeList";
import { HeaderScheme } from "./LayoutSchemes";
import { LockedRow } from "./LockedRow";
import { SplitAtField } from "./SplitAtField";
import { useBuilderLink } from "../../../hooks/site-builder/useBuilderLink";
import { CART_LOOK_SELECTION } from "../../../lib/site-builder/selection";
import { brandModeLabel } from "../../../lib/site-view/header-brand-modes";
import { surfaceFillLabel } from "../../../lib/site-view/surface-fill-labels";
import type { SiteBrandDraft } from "../../../lib/site-view/types";

type Header = SiteTheme["header"];

interface Props {
  header: Header;
  brand: SiteBrandDraft;
  /** Цвета темы — для заливки и её контраста. */
  colors: SiteColors;
  onChange: (patch: Partial<Header>) => void;
}

/** Места логотипа: подпись карточки и короткая — для сводки группы. */
const POSITIONS: ReadonlyArray<{ value: Header["logoPosition"]; label: string; short: string }> = [
  { value: "left", label: "Логотип слева", short: "логотип слева" },
  { value: "center", label: "Логотип по центру", short: "логотип по центру" },
  { value: "right", label: "Логотип справа", short: "логотип справа" },
  { value: "above", label: "Логотип над меню", short: "логотип над меню" },
];

const MENU_CASES: ReadonlyArray<{ value: Header["menuCase"]; label: string }> = [
  { value: "asis", label: "Как написано" },
  { value: "upper", label: "ПРОПИСНЫЕ" },
];

export function HeaderTab({ header, brand, colors, onChange }: Props) {
  const link = useBuilderLink();
  const settings = link({ panel: "settings", sub: "brand" });
  const transparent = header.tone === "transparent";
  // По тону заливки: у прозрачной шапки тёмная заливка видна после прокрутки.
  const noDarkLogo = resolveSurfaceFill(header.fill, { colors }).tone === "dark" && !brand.logoDark;
  const contrast = fillContrastIssue(header.fill, { colors });
  const position = POSITIONS.find((p) => p.value === header.logoPosition)?.short ?? "";

  return (
    <SettingsGroups panel="header" defaultCollapsed={["always"]}>
      <SettingsGroup
        id="layout"
        title="Расположение"
        hint="Где логотип и как ведёт себя шапка."
        summary={header.sticky ? `${position} · закреплена` : position}
      >
        <OptionCards
          aria-label="Место логотипа"
          columns={2}
          value={header.logoPosition}
          onChange={(logoPosition) => onChange({ logoPosition })}
          options={POSITIONS.map((p) => ({ value: p.value, label: p.label, preview: <HeaderScheme position={p.value} /> }))}
        />
        {header.logoPosition === "center" ? (
          <>
            <CheckRow checked={header.logoOversize} onChange={(logoOversize) => onChange({ logoOversize })}>
              Логотип крупнее шапки, выступает вниз
            </CheckRow>
            <SplitAtField value={header.splitAt} onChange={(splitAt) => onChange({ splitAt })} />
          </>
        ) : null}
        <CheckRow checked={header.sticky} onChange={(sticky) => onChange({ sticky })}>
          Закреплять шапку при прокрутке
        </CheckRow>
      </SettingsGroup>

      <SettingsGroup
        id="fill"
        title="Заливка"
        hint="Цвет фона шапки."
        summary={transparent ? `${surfaceFillLabel(header.fill)} · прозрачная над фото` : surfaceFillLabel(header.fill)}
        status={noDarkLogo || (contrast !== null && !contrast.ok) ? "warning" : undefined}
      >
        <SurfaceFillField
          label="Заливка шапки"
          value={header.fill}
          colors={colors}
          // У прозрачной шапки тон остаётся «transparent»: заливка — вид после прокрутки.
          onChange={(fill, tone) => onChange(transparent ? { fill } : { fill, tone })}
        />
        <div className="flex flex-col">
          <CheckRow checked={transparent} onChange={(on) => onChange({ tone: on ? "transparent" : resolveSurfaceFill(header.fill, { colors }).tone })}>
            Прозрачная поверх фото
          </CheckRow>
          {transparent ? (
            <FieldHint className="mt-0">
              Прозрачная — только над первым блоком с фото. После прокрутки, в каталоге, товаре, корзине, заказе и кабинете — с заливкой выше.
            </FieldHint>
          ) : null}
        </div>
        {noDarkLogo ? (
          <NoticeStrip tone="warning" action={<Link to={settings} className="font-medium text-primary hover:underline">Загрузить логотип</Link>}>
            Шапка тёмная, а логотипа для тёмного фона нет — на сайте будет название текстом.
          </NoticeStrip>
        ) : null}
      </SettingsGroup>

      <SettingsGroup id="brand" title="Бренд в шапке" tocLabel="Бренд" hint="Что из бренда видно в шапке." summary={brandModeLabel(header.brandMode)}>
        <BrandModeList value={header.brandMode} brand={brand} settingsHref={settings} onChange={(brandMode) => onChange({ brandMode })} />
      </SettingsGroup>

      <SettingsGroup
        id="menu"
        title="Пункты меню"
        tocLabel="Меню"
        hint="Как написаны пункты; сами пункты — в «Меню»."
        summary={MENU_CASES.find((c) => c.value === header.menuCase)?.label}
      >
        <div className="flex flex-col gap-2">
          <Label>Регистр</Label>
          <Segmented aria-label="Регистр пунктов меню" options={[...MENU_CASES]} value={header.menuCase} onChange={(menuCase) => onChange({ menuCase })} />
        </div>
        <Link to={link({ panel: "menu", sub: "header" })} className="type-body inline-flex min-h-12 items-center self-start font-medium text-primary hover:underline">
          Изменить пункты в «Меню» →
        </Link>
      </SettingsGroup>

      <SettingsGroup id="always" title="Всегда в шапке" tocLabel="Всегда" status="readonly" summary="вход и корзина">
        <LockedRow>Вход в кабинет покупателя</LockedRow>
        <div className="flex flex-wrap items-center justify-between gap-x-3">
          <LockedRow>Корзина со счётчиком</LockedRow>
          {/* SB120 этап 4: вид корзины — на экране «Корзина» (uiux-cart §3). */}
          <Link to={link(CART_LOOK_SELECTION)} className="type-body inline-flex min-h-12 items-center font-medium text-primary hover:underline">
            Вид корзины →
          </Link>
        </div>
        <FieldHint className="mt-0">Их ставит платформа: без них покупатель не найдёт корзину и свои заказы.</FieldHint>
      </SettingsGroup>
    </SettingsGroups>
  );
}
